import { logger } from '../utils/logger';

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: string;
}

export interface WebSocketConfig {
  url: string;
  token?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export interface ConnectionStats {
  isConnected: boolean;
  connectionId?: string;
  reconnectAttempts: number;
}

export class WebSocketClient {
  private ws?: WebSocket | undefined;
  private url: string;
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private reconnectAttempts: number = 0;
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private isConnected: boolean = false;
  private authToken?: string;

  constructor(config: WebSocketConfig) {
    this.url = config.url;
    this.reconnectInterval = config.reconnectInterval || 5000;
    this.maxReconnectAttempts = config.maxReconnectAttempts || 5;
  }

  setAuthToken(token: string) {
    this.authToken = token;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = this.authToken
          ? `${this.url}?token=${this.authToken}`
          : this.url;

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          logger.info('WebSocket connected');
          this.connectionCallbacks.forEach(callback => callback(true));
          resolve();
        };

        this.ws.onmessage = event => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = () => {
          this.isConnected = false;
          logger.info('WebSocket disconnected');
          this.connectionCallbacks.forEach(callback => callback(false));
          this.attemptReconnect();
        };

        this.ws.onerror = error => {
          console.error('WebSocket error:', error);
          const errorObj = new Error('WebSocket error');
          this.errorCallbacks.forEach(callback => callback(errorObj));
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    this.isConnected = false;
  }

  private attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      logger.info(
        `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`
      );

      setTimeout(() => {
        this.connect().catch(console.error);
      }, this.reconnectInterval);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  private handleMessage(message: WebSocketMessage) {
    const listeners = this.listeners.get(message.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(message.data);
        } catch (error) {
          console.error('Error in WebSocket message listener:', error);
        }
      });
    }
  }

  subscribe(messageType: string, listener: (data: any) => void) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, new Set());
    }
    this.listeners.get(messageType)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(messageType);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(messageType);
        }
      }
    };
  }

  send(type: string, data: any) {
    if (!this.isConnected || !this.ws) {
      console.warn('WebSocket is not connected');
      return;
    }

    const message: WebSocketMessage = {
      type,
      data,
      timestamp: new Date().toISOString(),
    };

    this.ws.send(JSON.stringify(message));
  }

  // Base methods for specialized clients - removed to avoid conflicts

  get connected() {
    return this.isConnected;
  }

  // Event handling methods
  private connectionCallbacks: Set<(connected: boolean) => void> = new Set();
  private errorCallbacks: Set<(error: Error) => void> = new Set();

  onConnection(callback: (connected: boolean) => void) {
    this.connectionCallbacks.add(callback);
    return () => this.connectionCallbacks.delete(callback);
  }

  offConnection(callback: (connected: boolean) => void) {
    this.connectionCallbacks.delete(callback);
  }

  onError(callback: (error: Error) => void) {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback);
  }

  offError(callback: (error: Error) => void) {
    this.errorCallbacks.delete(callback);
  }

  onMessage(
    messageType: string,
    callback: (message: WebSocketMessage) => void
  ) {
    return this.subscribe(messageType, callback);
  }

  offMessage(
    messageType: string,
    callback: (message: WebSocketMessage) => void
  ) {
    // Implementation for removing message callback
    const listeners = this.listeners.get(messageType);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  getConnectionStats(): ConnectionStats {
    const stats: ConnectionStats = {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
    };
    if (this.authToken) {
      stats.connectionId = this.authToken;
    }
    return stats;
  }

  sendMessage(message: WebSocketMessage) {
    this.send(message.type, message.data);
  }
}

// Specialized WebSocket clients
export class NotificationWebSocketClient extends WebSocketClient {
  constructor(config: WebSocketConfig) {
    super(config);
  }

  subscribeToNotifications(listener: (notification: any) => void) {
    return this.subscribe('notification', listener);
  }

  onNotification(callback: (notification: any) => void) {
    return this.subscribeToNotifications(callback);
  }
}

export class RecipeWebSocketClient extends WebSocketClient {
  constructor(
    config: WebSocketConfig,
    private recipeId: string
  ) {
    super(config);
  }

  subscribeToRecipeUpdates(listener: (update: any) => void) {
    return this.subscribe(`recipe_update_${this.recipeId}`, listener);
  }

  subscribeToComments(listener: (comment: any) => void) {
    return this.subscribe(`recipe_comments_${this.recipeId}`, listener);
  }

  subscribeToLikes(listener: (like: any) => void) {
    return this.subscribe(`recipe_likes_${this.recipeId}`, listener);
  }

  joinRecipeRoom() {
    this.send('join_room', { room: `recipe_${this.recipeId}` });
  }

  leaveRecipeRoom() {
    this.send('leave_room', { room: `recipe_${this.recipeId}` });
  }

  sendComment(comment: string) {
    this.send('comment', { recipe_id: this.recipeId, content: comment });
  }

  sendLike() {
    this.send('like', { recipe_id: this.recipeId });
  }

  onRecipeUpdate(callback: (update: any) => void) {
    return this.subscribeToRecipeUpdates(callback);
  }
}

export class ChatWebSocketClient extends WebSocketClient {
  constructor(
    config: WebSocketConfig,
    private roomId: string
  ) {
    super(config);
  }

  subscribeToMessages(listener: (message: any) => void) {
    return this.subscribe(`chat_${this.roomId}`, listener);
  }

  joinChatRoom() {
    this.send('join_room', { room: `chat_${this.roomId}` });
  }

  leaveChatRoom() {
    this.send('leave_room', { room: `chat_${this.roomId}` });
  }

  sendChatMessage(message: string) {
    this.send('chat_message', { room_id: this.roomId, content: message });
  }

  onChatMessage(callback: (message: any) => void) {
    return this.subscribeToMessages(callback);
  }
}

// Create a default instance
export const wsClient = new WebSocketClient({
  url: process.env['NEXT_PUBLIC_WS_URL'] || 'ws://localhost:4000/ws',
});
